import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Info, ServerCrash } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { authenticatedFetch } from '../context/apiClient';
//import { AlertTriangle, TrendingUp, Wrench, Rocket } from 'lucide-react';

export default function ModelTrend() {

   const [detailedPage, setDetailedPage] = useState(1);
  const [summaryPage, setSummaryPage] = useState(1);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState(null);
  const { user, logout } = useAuth();
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);

  useEffect(() => {
    const fetchModels = async () => {
      if (!user?.token) return;
      try {
        const response = await authenticatedFetch('/api/models', {}, user.token, logout);
        const data = await response.json();
        const distinct = [...new Map(data.map(item => [item.id, item])).values()];

        setModels(distinct);
        if (distinct.length > 0) {
          setSelectedModel(distinct[0].id);
        }
      } catch (err) {
        setModelsError('Failed to fetch models.');
      } finally {
        setModelsLoading(false);
      }
    };
    fetchModels();
  }, [user, logout]);

  useEffect(() => {
    const fetchReportData = async () => {
      if (!selectedModel || !user?.token) return;
      setReportLoading(true);
      setReportError(null);
      setReportData(null);
      try {
        const response = await authenticatedFetch(`/api/modeltrends-report/${selectedModel}`, {}, user.token, logout);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setReportData(data);
      } catch (err) {
        setReportError('Failed to fetch report data.');
        console.error(err);
      } finally {
        setReportLoading(false);
      }
    };
    fetchReportData();
  }, [selectedModel, user, logout]);

  const { overviewData, alerts, detailedAnalysisData, categorySummaryData } = reportData || {};

  const performanceData = useMemo(() => {
    if (!reportData?.lineChartData) return [];
    const { seriesData, xAxisData } = reportData.lineChartData;
    return xAxisData.map((day, index) => {
      const dataPoint = { day };
      seriesData.forEach(series => {
        dataPoint[series.name] = series.data[index];
      });
      return dataPoint;
    });
  }, [reportData]);

  const heatmapData = useMemo(() => {
    if (!reportData?.heatmapData) return [];
    const { categories, data } = reportData.heatmapData;
    return categories.map((category, index) => ({
      category,
      days: data[index],
    }));
  }, [reportData]);

  const detailedData = detailedAnalysisData || [];
  const summaryData = categorySummaryData || [];

  const itemsPerPage = 10;
  const detailedTotalPages = Math.ceil(detailedData.length / itemsPerPage);
  const summaryTotalPages = Math.ceil(summaryData.length / itemsPerPage);

  const getDetailedPageData = () => {
    const start = (detailedPage - 1) * itemsPerPage;
    return detailedData.slice(start, start + itemsPerPage);
  };

  const getSummaryPageData = () => {
    const start = (summaryPage - 1) * itemsPerPage;
    return summaryData.slice(start, start + itemsPerPage);
  };

  const getPerformanceColor = (performance) => {
    if (performance >= 70) return 'text-green-600';
    if (performance >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceBarWidth = (performance) => {
    return `${performance}%`;
  };

  // Function to get color based on value
  const getHeatmapColor = (value) => {
    if (value >= 95) return '#312e81'; // darkest blue
    if (value >= 90) return '#4338ca';
    if (value >= 85) return '#6366f1';
    if (value >= 80) return '#818cf8';
    return '#a5b4fc'; // lightest blue
  };

  const getFindingStyles = (type) => {
    switch (type) {
      case 'danger':
        return { icon: <AlertTriangle className="w-5 h-5" />, color: 'text-red-500' }; // Danger Style
      case 'success':
        return { icon: <CheckCircle2 className="w-5 h-5" />, color: 'text-green-500' }; // Success Style
      case 'warning':
        return { icon: <AlertTriangle className="w-5 h-5" />, color: 'text-yellow-500' }; // Warning Style
      case 'info':
        return { icon: <Info className="w-5 h-5" />, color: 'text-blue-500' }; // Info Style
      default:
        return { icon: <Info className="w-5 h-5" />, color: 'text-gray-500' };
    }
  };




  return (
  <div className="min-h-screen  p-6">
    <div className="max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-indigo-600 mb-4">Model Selection</h1>
        <div className="bg-white rounded-lg shadow-sm p-4">
          {modelsLoading ? (
            <div className="text-sm text-gray-500">Loading models...</div>
          ) : modelsError ? (
            <div className="text-sm text-red-500">{modelsError}</div>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-2">
              {models.map((model) => (
                <div 
                  key={model.id} 
                  className={`min-w-[200px] border-2 rounded-lg p-3 cursor-pointer transition-all ${
                    selectedModel === model.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedModel(model.id)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="truncate text-sm font-medium text-gray-700" title={model.name}>{model.name}</span>
                  </div>
                  {model.version && <div className="text-xs text-gray-500 mt-2">{model.version}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-indigo-600 mb-4 ml-6">Overall jailbreak rate comparison</h3>

      {reportLoading && <div className="text-center p-10">Loading report data...</div>}
      {reportError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-6" role="alert">
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline ml-2">{reportError}</span>
        </div>
      )}

      {reportData && !reportLoading && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Overall Performance</h3>
              <div className="flex items-baseline">
                <span className={`text-3xl font-bold ${overviewData.overallPerformance.trend === 'negative' ? 'text-red-500' : 'text-green-500'}`}>{overviewData.overallPerformance.value}</span>
                <span className="ml-2 text-sm text-gray-500">{overviewData.overallPerformance.change} from last period</span>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Total Test Queries</h3>
              <div className="flex items-baseline">
                <span className="text-3xl font-bold text-orange-500">{overviewData.totalQueries.value}</span>
                <span className="ml-2 text-sm text-gray-500">{overviewData.totalQueries.description}</span>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Best Category</h3>
              <div className="flex items-baseline">
                <span className="text-lg font-bold text-indigo-600">{overviewData.bestCategory.name}</span>
                <span className="ml-2 text-sm text-gray-500">{overviewData.bestCategory.accuracy} accuracy</span>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Test Methods</h3>
              <div className="flex items-baseline">
                <span className="text-3xl font-bold text-teal-500">{overviewData.testMethods.count}</span>
                <span className="ml-2 text-sm text-gray-500">{overviewData.testMethods.description}</span>
              </div>
            </div>
          </div>

      {/* Alert Messages */}
      {/* <div className="space-y-2 mb-6">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-500">
            Computer Vision performance dropped by 11.2% on 2024-02-01 - investigate potential data quality issues
          </p>
        </div>
        
        <div className="flex items-start">
          <TrendingUp className="w-5 h-5 text-teal-500 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-teal-600">
            NLP category showing consistent improvement trend (+5.0% over last month)
          </p>
        </div>
        
        <div className="flex items-start">
          <Wrench className="w-5 h-5 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-orange-600">
            Method C showing inconsistent results across different categories - consider parameter tuning
          </p>
        </div>
        
        <div className="flex items-start">
          <Rocket className="w-5 h-5 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-indigo-600">
            Model stability improved by 8.5% after recent optimization - performance variance decreased significantly
          </p>
        </div>
      </div> */}

      {/* Charts Section */}
      <div className="grid grid-cols-2 gap-6">
        {/* Line Chart */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-indigo-600 mb-4">LLM Model Performance Over Time</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis domain={[75, 100]} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line type="monotone" dataKey="Overall" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Cyberbullying and Harassment" stroke="#14b8a6" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Financial Crimes" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Hacking and Cybercrime" stroke="#84cc16" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Hate Speech and Disinformation" stroke="#eab308" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Identity Theft and Fraud" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Privacy Violations" stroke="#ec4899" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Social Engineering and Phishing" stroke="#a855f7" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Violence and Terrorism" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Heatmap */}
        <div className="bg-white  p-6">
                        
              <h3 className="text-lg font-semibold text-indigo-600 mb-4">
                {/* <AlertTriangle className="w-5 h-5 text-yellow-500" /> */}
                Key Findings
              </h3>
              <div className="space-y-3">
                {alerts.map((finding, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg text-sm">
                    <div className={`flex-shrink-0 mt-0.5 ${getFindingStyles(finding.type).color}`}>
                      {getFindingStyles(finding.type).icon}
                    </div>
                    <div>
                      <p className="text-gray-700 text-xs">{finding.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            
        </div>
      </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden mt-5">
          <div className="px-6 py-4">
            <h2 className="text-lg font-semibold text-indigo-600">Detailed performance analysis</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-indigo-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Method</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Queries Tested</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Performance</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getDetailedPageData().map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{row.date}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{row.category}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                        {row.method}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{row.queries}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getPerformanceColor(row.performance)} bg-opacity-10`}>
                        {row.performance}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-2">
            <button
              onClick={() => setDetailedPage(Math.max(1, detailedPage - 1))}
              disabled={detailedPage === 1}
              className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            {[...Array(detailedTotalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setDetailedPage(i + 1)}
                className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                  detailedPage === i + 1
                    ? 'bg-indigo-600 text-white'
                    : 'hover:bg-gray-200 text-gray-700'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setDetailedPage(Math.min(detailedTotalPages, detailedPage + 1))}
              disabled={detailedPage === detailedTotalPages}
              className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Category Performance Summary */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mt-5">
          <div className="px-6 py-4">
            <h2 className="text-lg font-semibold text-indigo-600">Category Performance Summary</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-purple-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Avg Performance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Best Method</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Worst Method</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Trend</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getSummaryPageData().map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{row.name}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-xs">
                          <div
                            className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: getPerformanceBarWidth(row.avgPerformance) }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-700">{row.avgPerformance}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                        {row.bestMethod}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {row.worstMethod}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {row.trend === 'Improving' ? (
                          <>
                            <TrendingUp size={16} className="text-blue-600" />
                            <span className="text-sm font-medium text-blue-600">{row.trend}</span>
                          </>
                        ) : (
                          <>
                            <TrendingDown size={16} className="text-red-600" />
                            <span className="text-sm font-medium text-red-600">{row.trend}</span>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-2">
            <button
              onClick={() => setSummaryPage(Math.max(1, summaryPage - 1))}
              disabled={summaryPage === 1}
              className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            {[...Array(summaryTotalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setSummaryPage(i + 1)}
                className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                  summaryPage === i + 1
                    ? 'bg-purple-600 text-white'
                    : 'hover:bg-gray-200 text-gray-700'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setSummaryPage(Math.min(summaryTotalPages, summaryPage + 1))}
              disabled={summaryPage === summaryTotalPages}
              className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
        </>
      )}

    </div>
   </div> 
  );
}